import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { ComputedRef, Ref } from 'vue'
import { onBeforeUnmount, onMounted, readonly, shallowRef, toValue, watch } from 'vue'

import { isRecord } from '~/types/api'

dayjs.extend(utc)

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>

interface ServerNowTickerOptions {
  enabled?: MaybeRef<boolean>
}

export function useServerClock() {
  const offsetMs = useState<number>('server-clock-offset-ms', () => 0)
  const isSynced = useState<boolean>('server-clock-synced', () => false)

  const syncServerTime = (serverTime: unknown): boolean => {
    if (typeof serverTime !== 'string' || !serverTime.trim()) {
      return false
    }

    const serverMs = Date.parse(serverTime)
    if (!Number.isFinite(serverMs)) {
      return false
    }

    offsetMs.value = serverMs - Date.now()
    isSynced.value = true
    return true
  }

  const nowMs = (): number => Date.now() + offsetMs.value
  // UTC mode keeps `now` consistent with the auction schedule values, which are
  // formatted with dayjs.utc() to match the UTC Filament admin picker.
  // Comparisons/diffs are absolute regardless of mode; the explicit UTC avoids
  // accidental local-timezone formatting if `now` is ever displayed.
  const nowDayjs = (): Dayjs => dayjs.utc(nowMs())
  const syncFromPayload = (payload: unknown): boolean => {
    if (!isRecord(payload)) {
      return false
    }

    return syncServerTime(payload.server_time)
  }

  return {
    offsetMs: readonly(offsetMs),
    isSynced: readonly(isSynced),
    syncServerTime,
    syncFromPayload,
    nowMs,
    nowDayjs,
  }
}

export function useServerNowTicker(options: ServerNowTickerOptions = {}) {
  const { nowDayjs } = useServerClock()
  const now = shallowRef<Dayjs>(nowDayjs())
  let ticker: ReturnType<typeof setInterval> | null = null

  const isEnabled = (): boolean => toValue(options.enabled ?? true)

  const updateNow = (): void => {
    now.value = nowDayjs()
  }

  const stop = (): void => {
    if (!ticker) {
      return
    }

    clearInterval(ticker)
    ticker = null
  }

  const start = (): void => {
    if (ticker || !isEnabled()) {
      return
    }

    updateNow()
    ticker = setInterval(updateNow, 1000)
  }

  onMounted(start)
  onBeforeUnmount(stop)

  if (options.enabled !== undefined) {
    watch(
      () => isEnabled(),
      (enabled) => {
        if (enabled) {
          start()
          return
        }

        stop()
      },
    )
  }

  return {
    now,
    updateNow,
    start,
    stop,
  }
}
